import { useCallback, useEffect, useState } from 'react';
import {
  getCompletenessAudit, getMaintenanceRuns, runRepairJobs,
  runAllForClient,
  type CompletenessAuditDto, type MaintenanceRun, type RepairJobsResult,
} from '../lib/api';
import { GBIBtn, T, sharedStyles } from './Shared';
import type { Client } from '../types';

// Which audit.summary counters we surface as chips, and how to humanize them.
const SUMMARY_LABELS: Record<string, string> = {
  maps_incomplete: 'Maps incomplete',
  ai_visibility_incomplete: 'AI visibility incomplete',
  gbp_reports_missing: 'GBP reports missing',
  campaign_incomplete: 'Campaign incomplete',
  insights_missing: 'Insights missing',
  ready_intakes_unimported: 'Ready intakes unimported',
  orphan_intakes: 'Orphan intakes',
  stuck_jobs: 'Stuck jobs',
};

function humanize(key: string): string {
  return SUMMARY_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; audit: CompletenessAuditDto; runs: MaintenanceRun[] };

export function MaintenancePanel({ clients, onReload, onClose }: {
  clients: Client[];
  onReload?: () => void;
  onClose: () => void;
}) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<RepairJobsResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const [audit, runs] = await Promise.all([getCompletenessAudit(), getMaintenanceRuns()]);
      setState({ status: 'ready', audit, runs });
    } catch (err) {
      setState({ status: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRun = async () => {
    if (state.status !== 'ready') return;
    const n = state.audit.plan.length;
    if (n === 0) return;
    if (!confirm(`Run Check & Update for ${n} location${n === 1 ? '' : 's'}? This will spend ~${state.audit.estimate.localFalconCredits} Local Falcon credits, ${state.audit.estimate.dataForSeoAiPairs} AI scans, and regenerate ${state.audit.estimate.claudeReports} report(s).`)) return;
    setRunning(true);
    setRunError(null);
    setRunResult(null);
    try {
      const result = await runRepairJobs(state.audit.planHash);
      setRunResult(result);
      await load();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('plan_hash_mismatch')) {
        setRunError('The audit changed — reloading…');
        await load();
        setRunError(null);
      } else {
        setRunError(message);
      }
    } finally {
      setRunning(false);
    }
  };

  const handleRefreshAll = async () => {
    if (refreshingAll || clients.length === 0) return;
    const message = `Refresh data for all ${clients.length} locations?\n\nThis refreshes Google Places, pulls completed Local Falcon Maps results, updates stale DataForSEO AI visibility, and regenerates Insights for every location. Some provider calls may incur charges.`;
    if (!confirm(message)) return;

    setRefreshingAll(true);
    let complete = 0;
    const failures: string[] = [];
    for (const [index, client] of clients.entries()) {
      setRefreshProgress(`Updating ${index + 1}/${clients.length}: ${client.name}…`);
      try {
        const result = await runAllForClient(client.id);
        const failedSteps = result.steps.filter(step => step.status === 'failed');
        if (failedSteps.length > 0) failures.push(`${client.name} (${failedSteps.map(step => step.step).join(', ')})`);
        else complete += 1;
      } catch {
        failures.push(client.name);
      }
    }
    setRefreshProgress(`Updated ${complete}/${clients.length} locations${failures.length ? ` · ${failures.length} need attention: ${failures.join(', ')}` : ''}.`);
    setRefreshingAll(false);
    onReload?.();
  };

  const busy = running || refreshingAll;

  return (
    <div onClick={busy ? undefined : onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 28, width: 620, maxWidth: '92vw',
        maxHeight: '86vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 20, fontWeight: 700, color: T.fgPrimary }}>
            Data Health &amp; Updates
          </div>
          <button onClick={onClose} disabled={busy} style={{
            background: 'transparent', border: 'none', cursor: busy ? 'wait' : 'pointer',
            fontSize: 18, color: T.fgMuted, lineHeight: 1,
          }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: T.fgMuted, marginBottom: 20, lineHeight: 1.5 }}>
          Check every location for missing Maps/AI data, stale reports, and stuck onboarding. Review the
          estimate below before creating a targeted repair batch that runs in the cloud.
        </div>

        {state.status === 'loading' && (
          <div style={{ padding: 30, textAlign: 'center', color: T.fgMuted, fontSize: 13 }}>Loading audit…</div>
        )}

        {state.status === 'error' && (
          <div style={{
            padding: '12px 14px', background: 'rgba(232,80,26,0.08)',
            border: `1px solid ${T.magenta}`, borderRadius: 8, fontSize: 12, color: '#C03B0F',
          }}>
            <strong>Failed to load audit.</strong>
            <div style={{ marginTop: 6, fontFamily: sharedStyles.fontMono }}>{state.message}</div>
            <button onClick={load} style={{
              marginTop: 10, background: 'transparent', border: `1px solid ${T.border}`,
              borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', color: T.fgSecondary,
            }}>Retry</button>
          </div>
        )}

        {state.status === 'ready' && (
          <>
            {/* Audit summary */}
            <SectionLabel>Audit summary</SectionLabel>
            <div style={{ fontSize: 13, color: T.fgPrimary, marginBottom: 10 }}>
              <strong>{state.audit.plan.length}</strong> location{state.audit.plan.length === 1 ? '' : 's'} need work.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {Object.entries(state.audit.summary).filter(([, v]) => v > 0).map(([key, v]) => (
                <SummaryChip key={key} label={humanize(key)} value={v} />
              ))}
              {Object.values(state.audit.summary).every(v => v <= 0) && (
                <div style={{ fontSize: 12, color: T.fgMuted, fontStyle: 'italic' }}>No issues found.</div>
              )}
            </div>

            {/* Cost estimate */}
            <SectionLabel>Estimated cost of Check &amp; Update</SectionLabel>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20,
            }}>
              <EstimateBox label="Local Falcon credits" value={state.audit.estimate.localFalconCredits} />
              <EstimateBox label="AI scans" value={state.audit.estimate.dataForSeoAiPairs} />
              <EstimateBox label="Reports to regenerate" value={state.audit.estimate.claudeReports} />
            </div>

            {/* Run action */}
            <div style={{
              padding: '14px 16px', borderRadius: 10, marginBottom: 24,
              background: T.bgSubtle, border: `1px solid ${T.border}`,
            }}>
              {state.audit.plan.length === 0 ? (
                <div style={{ fontSize: 13, fontWeight: 600, color: T.green }}>Everything is up to date ✓</div>
              ) : (
                <GBIBtn variant="primary" onClick={handleRun} disabled={running}>
                  {running ? 'Running…' : `Run Check & Update (${state.audit.plan.length} locations)`}
                </GBIBtn>
              )}

              {runResult && (
                <div style={{
                  marginTop: 12, fontSize: 12.5, color: T.fgPrimary,
                  padding: '10px 12px', background: '#fff', border: `1px solid ${T.cyan}`, borderRadius: 8,
                }}>
                  <strong>{runResult.jobs.filter(j => j.created).length}</strong> repair job(s) created.
                  They now run in the cloud — this page can be closed.
                </div>
              )}

              {runError && (
                <div style={{
                  marginTop: 12, fontSize: 12, color: '#C03B0F',
                  padding: '10px 12px', background: 'rgba(232,80,26,0.08)', border: `1px solid ${T.magenta}`, borderRadius: 8,
                }}>{runError}</div>
              )}
            </div>

            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 18, marginBottom: 24 }}>
              <button onClick={() => setAdvancedOpen(open => !open)} style={{
                border: 'none', background: 'transparent', color: T.fgPrimary, padding: 0,
                cursor: 'pointer', fontSize: 13, fontWeight: 700,
              }}>{advancedOpen ? '▾' : '▸'} Advanced data actions</button>
              {advancedOpen && (
                <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 8, background: T.bgSubtle, border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 12, color: T.fgSecondary, lineHeight: 1.5, marginBottom: 10 }}>
                    Use this only when you intentionally need every location refreshed now. It runs Places,
                    pulls completed Maps results, refreshes stale AI Visibility, and regenerates Insights.
                    Provider calls may incur charges.
                  </div>
                  <GBIBtn variant="secondary" onClick={handleRefreshAll} disabled={refreshingAll || clients.length === 0}>
                    {refreshingAll ? 'Updating locations…' : `Refresh every location now (${clients.length})`}
                  </GBIBtn>
                  {refreshProgress && (
                    <div style={{ marginTop: 10, fontSize: 12, color: T.fgPrimary, lineHeight: 1.45 }}>
                      {refreshProgress}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Recent health checks */}
            <SectionLabel>Recent health checks</SectionLabel>
            {state.runs.length === 0 ? (
              <div style={{ fontSize: 12, color: T.fgMuted, fontStyle: 'italic' }}>
                No maintenance runs recorded yet — the cloud health check runs Tue &amp; Fri.
              </div>
            ) : (
              <div style={{
                maxHeight: 220, overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: 8,
              }}>
                {state.runs.map((run, i) => (
                  <div key={run.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '9px 12px', gap: 10,
                    borderTop: i === 0 ? 'none' : `1px solid ${T.border}`,
                    fontSize: 12,
                  }}>
                    <span style={{ color: T.fgPrimary }}>{new Date(run.ranAt).toLocaleString()}</span>
                    <span style={{ color: T.fgSecondary }}>
                      {run.jobsCreated} created / {run.jobsDeferred} deferred
                    </span>
                    <span style={{ color: T.fgMuted }}>
                      {run.clientsIncomplete}/{run.clientsTotal} incomplete
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: T.fgSecondary, marginBottom: 8,
    }}>{children}</div>
  );
}

function SummaryChip({ label, value }: { label: string; value: number }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 11.5, fontWeight: 600, color: T.amber,
      background: 'rgba(227,116,0,0.08)', border: `1px solid rgba(227,116,0,0.35)`,
      borderRadius: 8, padding: '4px 10px',
    }}>
      {label} <strong>{value}</strong>
    </span>
  );
}

function EstimateBox({ label, value }: { label: string; value: number }) {
  return (
    <div style={{
      border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', background: '#fff',
    }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: T.fgPrimary }}>{value}</div>
      <div style={{ fontSize: 10.5, color: T.fgMuted, marginTop: 2 }}>{label}</div>
    </div>
  );
}
